/**
 * How the dataset vocabulary is worded on screen.
 *
 * The enums themselves live in `@wend/shared` and are never re-declared; this
 * file only decides what each member is *called* and which semantic tone it
 * carries, in one place, so the list, the detail screen and the mapping step
 * cannot describe the same state three different ways.
 *
 * Tone is semantic and never decorative, and the label always says the state
 * too, so the colour is never the only carrier.
 *
 * ## Except a dataset's own status, whose tone is not here
 *
 * `DATASET_STATUS_DISPLAY` carries words only. Its tone moved to
 * `components/DatasetStatusBadge.tsx` in slice 66, when the status chips went
 * onto the Blueprint mapping and "Awaiting mapping" changed from neutral to
 * warn — that badge is the only thing that draws a dataset status, so it owns
 * the colour. The field was deleted here rather than left beside it: two tone
 * tables for one status is how the badge and this file came to disagree about
 * `pending` for the length of a slice, and an unread field that still compiles
 * is the one the next screen would pick up. The rejection reasons below keep
 * their tones, because the rejected-row report reads them directly.
 */
import type {
  ColumnType,
  ContactField,
  DatasetStatus,
  DatasetSummary,
  RejectedRowReason,
} from '@wend/shared';
import type { BadgeTone } from '@wend/ui';

/* -------------------------------------------------------------------------- */
/* Dataset status                                                              */
/* -------------------------------------------------------------------------- */

export interface StatusDisplay {
  readonly label: string;
  /** One sentence saying what is true right now, and what happens next. */
  readonly description: string;
}

export const DATASET_STATUS_DISPLAY: Readonly<Record<DatasetStatus, StatusDisplay>> = {
  pending: {
    label: 'Awaiting mapping',
    description:
      'The file was uploaded and its columns were read, but no mapping was confirmed — so nothing has been imported yet.',
  },
  processing: {
    label: 'Importing',
    description: 'The import is running. The counts below update as rows land.',
  },
  ready: {
    label: 'Ready',
    description: 'The import finished. Every row is either a contact or in the rejected-row report.',
  },
  failed: {
    label: 'Failed',
    description: 'The import stopped before it finished. Nothing from this file was imported.',
  },
};

/* -------------------------------------------------------------------------- */
/* Detected column types                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The detector infers more types than slice 1 can map onto. They are all shown
 * because they are what the file actually contains, and because an unmapped
 * column keeps its type when it lands in `extras`.
 */
export const COLUMN_TYPE_LABEL: Readonly<Record<ColumnType, string>> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Yes / no',
  date: 'Date',
  email: 'Email',
  phone: 'Phone',
};

/* -------------------------------------------------------------------------- */
/* Mapping targets                                                             */
/* -------------------------------------------------------------------------- */

export const CONTACT_FIELD_LABEL: Readonly<Record<ContactField, string>> = {
  name: 'Name',
  email: 'Email address',
  phone: 'Phone number',
};

export const CONTACT_FIELD_HINT: Readonly<Record<ContactField, string>> = {
  name: 'Shown wherever the contact is listed.',
  email: 'Normalised and used to reach them by email.',
  phone: 'Normalised to E.164, e.g. +919812345678.',
};

/* -------------------------------------------------------------------------- */
/* Which sheet a list came from                                                */
/* -------------------------------------------------------------------------- */

/** The separator the API composes a multi-sheet dataset name with. */
const SHEET_SUFFIX_SEPARATOR = ' — ';

/** Longest base name the API keeps before appending the sheet. */
const MAX_BASE_NAME_LENGTH = 120;

/** `contacts.xlsx` → `contacts`, matching how the API derives a default name. */
function baseName(sourceFilename: string): string {
  const dot = sourceFilename.lastIndexOf('.');
  const base = dot <= 0 ? sourceFilename : sourceFilename.slice(0, dot);
  const trimmed = base.trim();
  return trimmed === '' ? sourceFilename : trimmed.slice(0, MAX_BASE_NAME_LENGTH);
}

/**
 * The worksheet a dataset came from, or null for a CSV.
 *
 * Read straight off the record. Both `Dataset` and `DatasetSummary` carry
 * `sheetName`, so the list and the detail screen agree without either of them
 * having to infer anything from the composed dataset name.
 */
export function datasetSheetLabel(
  dataset: Pick<DatasetSummary, 'sheetName'>,
): string | null {
  const sheet = dataset.sheetName?.trim();
  return sheet === undefined || sheet === '' ? null : sheet;
}

/* -------------------------------------------------------------------------- */
/* Rejection reasons                                                           */
/* -------------------------------------------------------------------------- */

export interface RejectionDisplay {
  readonly label: string;
  readonly tone: BadgeTone;
  /** What a person can do about it, in the report's summary. */
  readonly remedy: string;
}

export const REJECTED_ROW_REASON_DISPLAY: Readonly<
  Record<RejectedRowReason, RejectionDisplay>
> = {
  empty_row: {
    label: 'Empty row',
    tone: 'neutral',
    remedy: 'Every cell in the row was blank. Usually spacing left over in the sheet.',
  },
  missing_contact_method: {
    label: 'No email or phone',
    tone: 'warn',
    remedy: 'A contact with neither can never be reached, so it is not stored.',
  },
  invalid_email: {
    label: 'Invalid email',
    tone: 'warn',
    remedy: 'Fix the address in the file and upload it again.',
  },
  invalid_phone: {
    label: 'Invalid phone',
    tone: 'warn',
    remedy: 'The number could not be read as a dialable one. Add the country code.',
  },
  row_limit_exceeded: {
    label: 'Past the row limit',
    tone: 'bad',
    remedy: 'Split the file and upload it in parts.',
  },
};
